import { useSyncExternalStore } from 'react';

import { useIsMobile } from '@/hooks/use-mobile';

// Dismissal is intentionally in-memory only — the "best on desktop" nudge should
// reappear on the next page load (refresh / fresh visit) rather than being
// permanently hidden, so we deliberately do NOT persist it. The module-level
// store keeps the two useMobileNotice() instances in the tab in sync (AppLayout
// gates `shouldShow`; MobileInterstitial calls `dismiss`) and is reset whenever
// the page reloads.
let dismissed = false;
const listeners = new Set();

function setDismissed(value) {
	dismissed = value;
	listeners.forEach((notify) => notify());
}

function subscribe(notify) {
	listeners.add(notify);
	return () => listeners.delete(notify);
}

function getSnapshot() {
	return dismissed;
}

/**
 * Drives the "Unify is best on desktop" interstitial. Shows on mobile until the
 * user dismisses it; the dismissal lasts for the current page session and the
 * interstitial returns on the next load. `reset()` / the `resetMobileNotice()`
 * helper re-show it immediately (e.g. a Settings toggle — NEA-5809).
 */
export function useMobileNotice() {
	const isMobile = useIsMobile();
	const isDismissed = useSyncExternalStore(subscribe, getSnapshot);

	return {
		shouldShow: isMobile && !isDismissed,
		isMobile,
		dismissed: isDismissed,
		dismiss: () => setDismissed(true),
		reset: () => setDismissed(false)
	};
}

export function resetMobileNotice() {
	setDismissed(false);
}
